import React, { useContext } from 'react';
import { useAuthFlowManager, StepName, FlowType, StepConfig } from './hooks/UseAuthFlowManager';
import LoginForm from './steps/LoginForm';
import EmailMiniForm from './steps/EmailMiniForm';
import SignupMiniForm from './steps/SignupMiniForm';
import VerifyEmailForm from './steps/VerifyEmailForm';
import ForgotPasswordConfirmMiniForm from './steps/ForgotPasswordConfirmMiniForm';
import SetupMFAForm from './steps/SetupMFAForm';
import VerifyMFAForm from './steps/VerifyMFAForm';
import { Box, Alert, Divider } from '@mui/material';
import { ThemeContext } from 'styled-components';
import PasswordConfirmationForm from "./steps/PasswordConfirmationForm";
import WithActions from "../../common/components/Wrappers/WithActions";

interface AuthFlowManagerProps {
    flowType?: FlowType;
    onStepTransition?: (step: StepName, error: string | null, data?: any) => void;
    initialStepConfig?: StepConfig;
    urlCode?: string | null;
    urlEmail?: string | null;
    prefillEmail?: string;
    onEmailChange?: (email: string) => void;
}

/**
 * AuthFlowManager - Unified component for managing authentication flows
 * Uses hooks consistently for all flow types
 */
const AuthFlowManager: React.FC<AuthFlowManagerProps> = ({
    flowType = 'login',
    onStepTransition: customOnStepTransition,
    initialStepConfig: customInitialStepConfig,
    urlCode,
    urlEmail,
    prefillEmail,
    onEmailChange
}) => {
    const theme = useContext(ThemeContext);

    // Use hook for all flows consistently
    const flowData = useAuthFlowManager({
        onStepTransition: customOnStepTransition || ((step, error, data) => {
            // Default step transition handling
        }),
        initialStepConfig: customInitialStepConfig || (() => {
            // Default step configurations based on flow type
            if (flowType === 'forgotPassword') {
                return {
                    email: 'enabled',
                    newPassword: 'hidden',
                    setupMFA: 'hidden',
                    verifyMFA: 'hidden'
                };
            } else if (flowType === 'signup') {
                return {
                    signup: 'enabled',
                    verifyEmail: 'hidden',
                    passwordConfirmation: 'hidden',
                    setupMFA: 'hidden',
                    verifyMFA: 'hidden'
                };
            } else if (flowType === 'confirm') {
                return {
                    verifyEmail: 'enabled',
                    login: 'hidden',
                    setupMFA: 'hidden',
                    verifyMFA: 'hidden'
                };
            }
            return {
                login: 'enabled',
                verifyEmail: 'hidden',
                setupMFA: 'hidden',
                verifyMFA: 'hidden'
            };
        })(),
        flowType,
        urlCode,
        urlEmail,
        prefillEmail,
        onEmailChange
    });

    const renderStep = (stepName: StepName, key: string) => {
        const stepEnabled = flowData.isStepEnabled(stepName);
        const stepDisabled = flowData.isStepDisabled(stepName);
        const isCurrentStep = flowData.currentStep === stepName;
        const stepData = flowData.getStepData(stepName);

        if (!stepData || !stepData.formData) {
            return <div>Loading...</div>;
        }

        const commonProps = {
            onStepTransition: stepEnabled ? flowData.handleStepTransition : () => {},
            onLoginSuccess: stepEnabled ? flowData.handleLoginSuccess : () => {},
            onLoginError: stepEnabled ? flowData.handleLoginError : () => {},
            isLoading: stepEnabled ? flowData.isLoading : false,
            setIsLoading: stepEnabled ? flowData.setIsLoading : () => {},
            csrfToken: flowData.csrfToken,
            error: stepEnabled ? flowData.loginError : null,
            disabled: stepDisabled,
            isVisible: flowData.isStepVisible(stepName),
            isActive: isCurrentStep
        };

        switch (stepName) {
            case 'login':
                return (
                    <LoginForm
                        key={key}
                        formData={stepData.formData as any}
                        disabled={stepDisabled}
                        onChangeAuthForm={stepData.onChangeAuthForm}
                        handleLogin={flowData.handleLogin}
                        {...commonProps}
                    />
                );
            case 'signup':
                return (
                    <SignupMiniForm
                        key={key}
                        formData={stepData.formData as any}
                        onChangeAuthForm={stepData.onChangeAuthForm}
                        onSubmit={flowData.handleSignupInfoSubmit}
                        disabled={flowData.currentStep !== 'signup'}
                        isLoading={flowData.isLoading}
                    />
                );
            case 'email':
                return (
                    <EmailMiniForm
                        key={key}
                        email={(stepData.formData as any).email || ''}
                        onChangeAuthForm={stepData.onChangeAuthForm}
                        onSubmit={flowData.handleEmailSubmit}
                        disabled={flowData.currentStep !== 'email'}
                        isLoading={flowData.isLoading}
                        isCompleted={flowData.currentStep !== 'email'}
                    />
                );
            case 'verifyEmail':
                return (
                    <VerifyEmailForm
                        key="verifyEmail"
                        formData={stepData.formData as any}
                        onChangeAuthForm={stepData.onChangeAuthForm}
                        handleLogin={flowData.handleLogin}
                        onConfirmSubmit={flowType === 'confirm' ? flowData.handleConfirmEmailSubmit : undefined}
                        isConfirmFlow={flowType === 'confirm'}
                        {...commonProps}
                    />
                );
            case 'newPassword':
                if (flowType === 'forgotPassword') {
                    return (
                        <ForgotPasswordConfirmMiniForm
                            key={key}
                            verificationCode={(stepData.formData as any).verificationCode || ''}
                            onChangeAuthForm={stepData.onChangeAuthForm}
                            password={(stepData.formData as any).newPassword || ''}
                            confirmPassword={(stepData.formData as any).confirmNewPassword || ''}
                            validateNewPasswordMatch={stepData.validateNewPasswordMatch}
                            onSubmit={flowData.handlePasswordSubmit}
                            onLoginError={flowData.handleLoginError}
                            disabled={flowData.currentStep !== 'newPassword'}
                            isLoading={flowData.isLoading}
                            currentStep={flowData.currentStep}
                        />
                    );
                } else {
                    return (
                        <PasswordConfirmationForm
                            key={key}
                            onChangeAuthForm={stepData.onChangeAuthForm}
                            disabled={false}
                            onSubmit={() => {}}
                            validatePasswordMatch={stepData.validatePasswordMatch}
                            onLoginError={() => {}}
                        />
                    );
                }
            case 'passwordConfirmation':
                return (
                    <PasswordConfirmationForm
                        key={key}
                        onChangeAuthForm={stepData.onChangeAuthForm}
                        validatePasswordMatch={stepData.validatePasswordMatch}
                        onSubmit={flowData.handleSignupSubmit}
                        onLoginError={flowData.handleLoginError}
                        disabled={flowData.currentStep !== 'passwordConfirmation'}
                        isLoading={flowData.isLoading}
                    />
                );
            case 'setupMFA':
                return (
                    <SetupMFAForm
                        key={key}
                        formData={stepData.formData as any}
                        onChangeAuthForm={stepData.onChangeAuthForm}
                        handleLogin={flowData.handleLogin}
                        email={(stepData as any).email}
                        password={(stepData as any).password}
                        showQR={true}
                        {...commonProps}
                    />
                );
            case 'verifyMFA':
                return (
                    <VerifyMFAForm
                        key={key}
                        formData={stepData.formData as any}
                        onChangeAuthForm={stepData.onChangeAuthForm}
                        {...commonProps}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                width: '100%',
                flex: 1,
                minHeight: 0
            }}
        >
            {/* Render all visible steps */}
            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                    width: '100%'
                }}
            >
                {(() => {
                    // Define steps for each flow type
                    const stepsToRender: StepName[] = flowType === 'forgotPassword'
                        ? ['email', 'newPassword', 'setupMFA', 'verifyMFA'] // Skip verifyEmail for forgot password flow
                        : flowType === 'signup'
                        ? ['signup', 'verifyEmail', 'passwordConfirmation', 'setupMFA', 'verifyMFA']
                        : flowType === 'confirm'
                        ? ['verifyEmail', 'login', 'setupMFA', 'verifyMFA']
                        : ['login', 'verifyEmail', 'newPassword', 'setupMFA', 'verifyMFA'];

                    const visibleSteps = stepsToRender.filter(stepName => {
                        return flowData.isStepVisible(stepName);
                    });

                    return visibleSteps.map((stepName, index) => {
                        const isCurrentStep = flowData.currentStep === stepName;
                        const isDisabled = flowData.isStepDisabled(stepName);
                        const isLastStep = index === visibleSteps.length - 1;
                        const hasMultipleSteps = visibleSteps.length > 1;

                        return (
                            <Box
                                key={stepName}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    flex: isCurrentStep ? 1 : 0,
                                    flexShrink: isCurrentStep ? 0 : 1,
                                    height: isCurrentStep ? '100%' : 'auto',
                                    minHeight: isCurrentStep ? 0 : 'auto',
                                    width: '100%',
                                    // opacity: isDisabled && !isCurrentStep ? 0.6 : 1,
                                    transition: 'all 0.3s ease'
                                }}
                            >
                                {renderStep(stepName, stepName)}
                                {hasMultipleSteps && !isLastStep && (
                                    <Divider
                                        sx={{
                                            my: 2,
                                            height: '1px',
                                            background: `linear-gradient(to right, transparent 0%, ${theme.colors.secondary} 0%, ${theme.colors.secondary} 65%, transparent 95%)`,
                                            border: 'none'
                                        }}
                                    />
                                )}
                            </Box>
                        );
                    });
                })()}
            </Box>

            {/* Validation Error Display - fixed height row to prevent layout shift */}
            <Box sx={{ flexShrink: 0 }}>
                <WithActions
                    forceSpacing={true}
                    showRefreshButton={false}
                    showSuccessButton={false}
                    showActionButton={false}
                    showRequiredIcon={false}
                >
                    <Box sx={{ height: 80, display: 'flex', alignItems: 'center', width: '100%' }}>
                        {flowData.loginError ? (
                            <Alert severity="error" sx={{ m: 0, width: '100%' }}>
                                {flowData.loginError}
                            </Alert>
                        ) : (
                            <Box sx={{ width: '100%' }} />
                        )}
                    </Box>
                </WithActions>
            </Box>

        </Box>
    );
};

export default AuthFlowManager;
